import { useMemo, type ReactNode } from "react";
import { Users } from "lucide-react";
import styles from "./Table.module.css";
import Spinner from "@/components/common/spinner/Spinner";
import type { Action, ColumnDef, tableProps } from "./types.props";
import Button from "../button/Button";

const EmptyState = () => (
    <div className={styles["empty-state"]}>
        <Users size={48} className={styles["empty-icon"]} />
        <p className={styles["empty-text"]}>No se encontraron resultados</p>
        <p className={styles["empty-subtext"]}>
            Intenta ajustar los filtros o añadir nuevos datos.
        </p>
    </div>
);

const TableHeader = <T,>({
    columns,
    hasActions,
    actionsHeaderLabel,
}: {
    columns: ColumnDef<T>[];
    hasActions: boolean;
    actionsHeaderLabel: string;
}) => (
    <thead>
        <tr className={styles["table-header-row"]}>
            {columns.map((col) => (
                <th
                    key={String(col.accessorKey)}
                    className={styles["table-header-cell"]}
                >
                    {col.header}
                </th>
            ))}
            {hasActions && (
                <th
                    className={`${styles["table-header-cell"]} ${styles["actions-header"]}`}
                >
                    {actionsHeaderLabel}
                </th>
            )}
        </tr>
    </thead>
);

const TableCell = <T,>({ column, row }: { column: ColumnDef<T>; row: T }) => {
    let content: ReactNode = "-";
    if (column.cell) content = column.cell(row);
    else if (column.accessorFn) content = column.accessorFn(row) as ReactNode;
    else if (column.accessorKey)
        content = (row as any)[column.accessorKey] as ReactNode;
    return (
        <td
            className={styles["table-cell"]}
            data-label={typeof column.header === "string" ? column.header : undefined}
        >
            {content}
        </td>
    );
};

const ActionButton = <T,>({ action, row }: { action: Action<T>; row: T }) => {
    const { handler, label, icon, variant, disabled, tooltip, isLoading } = action;

    const isDisabled = useMemo(() => {
        if (typeof disabled === "function") return disabled(row);
        return !!disabled;
    }, [disabled, row]);

    const currentTooltip = useMemo(() => {
        if (typeof tooltip === "function") return tooltip(row);
        return tooltip || label;
    }, [tooltip, label, row]);

    const isActionLoading = useMemo(() => {
        if (typeof isLoading === 'function') return isLoading(row);
        return !!isLoading;
    }, [isLoading, row]);

    return (
        <Button
            onClick={() => handler(row)}
            title={currentTooltip}
            disabled={isDisabled || isActionLoading}
            isLoading={isActionLoading}
            variant={variant === 'danger' ? 'secondary' : variant}
            className={`${styles["action-button"]} ${variant ? styles[`btn-${variant}`] : ''}`}
        >
            {icon}
            <span className={styles["action-label"]}>{label}</span>
        </Button>
    );
};

const TableRow = <T extends { id: any }>({
    row,
    index,
    columns,
    actions = [],
}: {
    row: T;
    index: number;
    columns: ColumnDef<T>[];
    actions?: Action<T>[];
}) => {
    const hasActions = actions.length > 0;
    return (
        <tr
            key={row.id ?? index}
            className={`${styles["table-row"]} ${index % 2 === 0 ? styles["table-row-even"] : ""}`}
        >
            {columns.map((col) => (
                <TableCell key={String(col.accessorKey)} column={col} row={row} />
            ))}
            {hasActions && (
                <td className={`${styles["table-cell"]} ${styles["actions-cell"]}`}>
                    <div className={styles["actions-container"]}>
                        {actions.map((action, i) => (
                            <ActionButton key={i} action={action} row={row} />
                        ))}
                    </div>
                </td>
            )}
        </tr>
    );
};

export const Table = <T extends { id: any }>({
    data,
    columns,
    isLoading = false,
    actions,
    actionsHeaderLabel = "Acciones",
}: tableProps<T>) => {
    const hasActions = Array.isArray(actions) && actions.length > 0;

    if (isLoading) return <Spinner text="Cargando datos..." />;
    if (!data || data.length === 0) return <EmptyState />;

    return (
        <div className={styles["table-container"]}>
            <div className={styles["table-wrapper"]}>
                <table className={styles.table}>
                    <TableHeader<T>
                        columns={columns}
                        hasActions={hasActions}
                        actionsHeaderLabel={actionsHeaderLabel}
                    />
                    <tbody>
                        {data.map((row, idx) => (
                            <TableRow<T>
                                key={row.id}
                                row={row}
                                index={idx}
                                columns={columns}
                                actions={actions}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
